import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Layers, ShoppingCart, Info } from "lucide-react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STACKING_SIZES = [
  { key: "2x2x2", name: "2ft × 2ft × 2ft", width: 2 },
  { key: "3x2x2", name: "3ft × 2ft × 2ft", width: 3 },
  { key: "4x2x2", name: "4ft × 2ft × 2ft", width: 4 },
  { key: "5x2x2", name: "5ft × 2ft × 2ft", width: 5 },
  { key: "6x2x2", name: "6ft × 2ft × 2ft", width: 6 },
  { key: "7x2x2", name: "7ft × 2ft × 2ft", width: 7 },
  { key: "8x2x2", name: "8ft × 2ft × 2ft", width: 8 },
];

const STACK_OPTIONS = [2, 3, 4, 5];

const WOOD_STAINS = {
  // PINE RANGE
  antique_stripped_pine: { name: "Antique Stripped Pine", hex: "#E8D4A8", category: "pine" },
  golden_pine: { name: "Golden Pine", hex: "#D4A84B", category: "pine" },
  dark_pine: { name: "Dark Pine", hex: "#8B6914", category: "pine" },
  // MAHOGANY RANGE
  brown_mahogany: { name: "Brown Mahogany", hex: "#5C3317", category: "mahogany" },
  very_red_mahogany: { name: "Very Red Mahogany", hex: "#8B2500", category: "mahogany" },
  dark_red_mahogany: { name: "Dark Red Mahogany", hex: "#5C1A0B", category: "mahogany" },
  // OAK RANGE
  light_oak: { name: "Light Oak", hex: "#C9A86C", category: "oak" },
  golden_oak: { name: "Golden Oak", hex: "#B8860B", category: "oak" },
  medium_oak: { name: "Medium Oak", hex: "#A0785A", category: "oak" },
  french_oak: { name: "French Oak", hex: "#8B7355", category: "oak" },
  english_oak: { name: "English Oak", hex: "#6B4423", category: "oak" },
  // WALNUT & ROSEWOOD
  dark_walnut: { name: "Dark Walnut", hex: "#4A3728", category: "walnut" },
  light_rosewood: { name: "Light Rosewood", hex: "#9C6B5C", category: "rosewood" },
  light_sycamore: { name: "Light Sycamore", hex: "#F5DEB3", category: "sycamore" },
  // BLACK RANGE
  black_black: { name: "Black Black", hex: "#1A1A1A", category: "black" },
  black_ebony: { name: "Black Ebony", hex: "#2D2D2D", category: "black" },
  // GREY RANGE
  ironstone_grey: { name: "Ironstone Grey", hex: "#4A4A4A", category: "grey" },
  driftwood_grey: { name: "Driftwood Grey", hex: "#78736E", category: "grey" },
  moonlight_grey: { name: "Moonlight Grey", hex: "#B0B0B0", category: "grey" },
  dovetail_grey: { name: "Dovetail Grey", hex: "#6E6E6E", category: "grey" },
  // WHITE & SPECIAL
  soft_white: { name: "Soft White", hex: "#F5F5F0", category: "white" },
  police_box_blue: { name: "Police Box Blue", hex: "#003B6F", category: "special" },
  christmas_green: { name: "Christmas Green", hex: "#165B33", category: "special" },
  christmas_red: { name: "Christmas Red", hex: "#B31B1B", category: "special" },
  // NATURAL
  natural: { name: "Natural (Unfinished)", hex: "#E3C28D", category: "natural" },
};

const STAIN_CATEGORIES = [
  { id: "natural", name: "Natural" },
  { id: "pine", name: "Pine" },
  { id: "oak", name: "Oak" },
  { id: "mahogany", name: "Mahogany" },
  { id: "walnut", name: "Walnut" },
  { id: "rosewood", name: "Rosewood" },
  { id: "sycamore", name: "Sycamore" },
  { id: "grey", name: "Grey" },
  { id: "black", name: "Black" },
  { id: "white", name: "White" },
  { id: "special", name: "Special" },
];

const StackingPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { addToCart } = useCart();
  
  const [config, setConfig] = useState({
    size_key: "4x2x2",
    stack_count: 3,
    stain_color: "natural",
    glass_thickness: 6,
  });
  
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    calculatePrice();
  }, [config]);

  const calculatePrice = async () => {
    try {
      const response = await axios.post(`${API}/stacking/calculate`, config);
      setPricing(response.data);
    } catch (error) {
      console.error("Stacking pricing error:", error);
    }
  };

  const handleAddToCart = async () => {
    if (!user) {
      toast.error("Please login to add items to cart");
      navigate("/login");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(
        `${API}/cart/add-stack`,
        { stacking_config: config, quantity: 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Stacking unit added to cart!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add to cart");
    } finally {
      setLoading(false);
    }
  };

  const selectedSize = STACKING_SIZES.find(s => s.key === config.size_key);
  const stainHex = WOOD_STAINS[config.stain_color]?.hex || WOOD_STAINS.natural.hex;

  // SVG visualization
  const StackVisualization = useMemo(() => {
    const svgWidth = 400;
    const svgHeight = 350;
    const padding = 30;
    
    const unitHeight = 2; // All units are 2ft height
    const unitDepth = 2;
    const unitWidth = selectedSize?.width || 4;
    
    const totalHeight = unitHeight * config.stack_count;
    const maxDim = Math.max(unitWidth, totalHeight, unitDepth);
    const scale = (svgHeight - padding * 2) / (maxDim * 1.2);
    
    const isoX = (x, y, z) => svgWidth / 2 + (x - z) * scale * 0.866;
    const isoY = (x, y, z) => svgHeight - padding - y * scale + (x + z) * scale * 0.3;

    const units = [];
    for (let i = 0; i < config.stack_count; i++) {
      const yOffset = i * unitHeight;
      const w = unitWidth / 2;
      const h = unitHeight / 2;
      const d = unitDepth / 2;
      
      const corners = {
        bbl: [-w, yOffset, -d],
        bbr: [w, yOffset, -d],
        btl: [-w, yOffset + unitHeight, -d],
        btr: [w, yOffset + unitHeight, -d],
        fbl: [-w, yOffset, d],
        fbr: [w, yOffset, d],
        ftl: [-w, yOffset + unitHeight, d],
        ftr: [w, yOffset + unitHeight, d],
      };
      
      const point = (key) => {
        const [x, y, z] = corners[key];
        return `${isoX(x, y, z)},${isoY(x, y, z)}`;
      };

      units.push(
        <g key={i}>
          {/* Back panel */}
          <polygon
            points={`${point('bbl')} ${point('bbr')} ${point('btr')} ${point('btl')}`}
            fill={stainHex}
            stroke="#000"
            strokeWidth="0.5"
            opacity="0.9"
          />
          {/* Left panel */}
          <polygon
            points={`${point('bbl')} ${point('fbl')} ${point('ftl')} ${point('btl')}`}
            fill={stainHex}
            stroke="#000"
            strokeWidth="0.5"
            opacity="0.7"
          />
          {/* Right panel */}
          <polygon
            points={`${point('bbr')} ${point('fbr')} ${point('ftr')} ${point('btr')}`}
            fill={stainHex}
            stroke="#000"
            strokeWidth="0.5"
            opacity="0.8"
          />
          {/* Top panel */}
          <polygon
            points={`${point('btl')} ${point('btr')} ${point('ftr')} ${point('ftl')}`}
            fill={stainHex}
            stroke="#000"
            strokeWidth="0.5"
            opacity="0.6"
          />
          {/* Front glass */}
          <polygon
            points={`${point('fbl')} ${point('fbr')} ${point('ftr')} ${point('ftl')}`}
            fill="#88ccff"
            stroke="#aaddff"
            strokeWidth="1"
            opacity="0.3"
          />
          {/* Unit number */}
          <text
            x={isoX(0, yOffset + unitHeight / 2, d + 0.3)}
            y={isoY(0, yOffset + unitHeight / 2, d + 0.3)}
            fill="#F4F1DE"
            fontSize="10"
            textAnchor="middle"
            opacity="0.6"
          >
            #{i + 1}
          </text>
        </g>
      );
    }

    return (
      <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} className="w-full h-full max-h-[400px]">
        <defs>
          <filter id="stackGlow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Grid background */}
        <g opacity="0.1">
          {[...Array(12)].map((_, i) => (
            <line key={`h${i}`} x1="0" y1={i * 30} x2={svgWidth} y2={i * 30} stroke="#E07A5F" strokeWidth="0.5" />
          ))}
          {[...Array(14)].map((_, i) => (
            <line key={`v${i}`} x1={i * 30} y1="0" x2={i * 30} y2={svgHeight} stroke="#E07A5F" strokeWidth="0.5" />
          ))}
        </g>
        
        {/* Frame lines */}
        <g stroke="#666" strokeWidth="2" opacity="0.5">
          <line x1={isoX(-unitWidth/2 - 0.1, 0, unitDepth/2)} y1={isoY(-unitWidth/2 - 0.1, 0, unitDepth/2)} 
                x2={isoX(-unitWidth/2 - 0.1, totalHeight, unitDepth/2)} y2={isoY(-unitWidth/2 - 0.1, totalHeight, unitDepth/2)} />
          <line x1={isoX(unitWidth/2 + 0.1, 0, unitDepth/2)} y1={isoY(unitWidth/2 + 0.1, 0, unitDepth/2)} 
                x2={isoX(unitWidth/2 + 0.1, totalHeight, unitDepth/2)} y2={isoY(unitWidth/2 + 0.1, totalHeight, unitDepth/2)} />
        </g>
        
        {/* Stacked units */}
        <g filter="url(#stackGlow)">
          {units}
        </g>
        
        {/* Dimensions */}
        <text x={svgWidth / 2} y={svgHeight - 5} fill="#F4F1DE" fontSize="11" textAnchor="middle" opacity="0.6" fontFamily="Space Grotesk">
          {unitWidth}ft W × {totalHeight}ft H (total) × {unitDepth}ft D
        </text>
      </svg>
    );
  }, [config.size_key, config.stack_count, config.stain_color, selectedSize, stainHex]);

  return (
    <div className="min-h-screen pt-16 bg-[#0A0A0A]">
      <div className="flex flex-col lg:flex-row h-[calc(100vh-4rem)]">
        {/* Visualization */}
        <div className="flex-1 flex items-center justify-center p-4 relative" data-testid="stacking-viewer">
          {StackVisualization}
          
          {/* Info badges */}
          <div className="absolute top-4 left-4 flex flex-wrap gap-2">
            <span className="bg-[#121212]/80 backdrop-blur px-3 py-1 text-[#E07A5F] text-sm border border-white/10 flex items-center gap-2">
              <Layers className="w-4 h-4" />
              Stack of {config.stack_count}
            </span>
            <span className="bg-[#121212]/80 backdrop-blur px-3 py-1 text-[#F4F1DE]/60 text-sm border border-white/10">
              {selectedSize?.name}
            </span>
          </div>
        </div>

        {/* Control Panel */}
        <div className="w-full lg:w-[400px] glass-panel p-6 overflow-y-auto" data-testid="stacking-controls">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-2">
              Stacking Enclosures
            </h1>
            <p className="text-[#F4F1DE]/60 text-sm">
              Stack multiple enclosures vertically for efficient space use
            </p>
          </div>

          {/* Price Display */}
          <div className="mb-8 p-6 bg-[#0A0A0A] border border-white/10">
            <p className="text-[#F4F1DE]/60 text-sm uppercase tracking-widest mb-2">Total Price (inc. VAT)</p>
            <p className="text-4xl font-bold font-['Space_Grotesk'] text-[#E07A5F]" data-testid="stacking-total-price">
              £{pricing?.total?.toFixed(2) || "0.00"}
            </p>
            {pricing && (
              <div className="mt-4 space-y-1 text-sm text-[#F4F1DE]/50">
                <div className="flex justify-between">
                  <span>Unit Price (each)</span>
                  <span>£{pricing.unit_price.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Materials ({config.stack_count} units)</span>
                  <span>£{pricing.total_materials.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Metal Frame</span>
                  <span>£{pricing.frame_cost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Labour</span>
                  <span>£{pricing.total_labor.toFixed(2)}</span>
                </div>
                {pricing.discount_applied > 0 && (
                  <div className="flex justify-between text-green-400">
                    <span>Bulk Discount</span>
                    <span>-£{pricing.discount_applied.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-white/10">
                  <span>Subtotal</span>
                  <span>£{pricing.subtotal?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>VAT (20%)</span>
                  <span>£{pricing.vat?.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Size Selection */}
          <div className="mb-6">
            <Label className="text-[#F4F1DE]/80 mb-3 block">Enclosure Size</Label>
            <div className="grid grid-cols-2 gap-2">
              {STACKING_SIZES.map((size) => (
                <button
                  key={size.key}
                  onClick={() => setConfig(prev => ({ ...prev, size_key: size.key }))}
                  className={`p-3 border text-left text-sm ${
                    config.size_key === size.key 
                      ? "border-[#E07A5F] bg-[#E07A5F]/10 text-[#E07A5F]" 
                      : "border-white/10 bg-[#121212] text-[#F4F1DE]/60"
                  }`}
                  data-testid={`size-${size.key}`}
                >
                  {size.name}
                </button>
              ))}
            </div>
          </div>

          {/* Stack Count */}
          <div className="mb-6">
            <Label className="text-[#F4F1DE]/80 mb-3 block">Number of Units</Label>
            <div className="flex gap-2">
              {STACK_OPTIONS.map((count) => (
                <button
                  key={count}
                  onClick={() => setConfig(prev => ({ ...prev, stack_count: count }))}
                  className={`flex-1 py-3 border text-center font-bold ${
                    config.stack_count === count
                      ? "border-[#E07A5F] bg-[#E07A5F]/10 text-[#E07A5F]"
                      : "border-white/10 bg-[#121212] text-[#F4F1DE]/60"
                  }`}
                  data-testid={`stack-${count}`}
                >
                  {count}
                </button>
              ))}
            </div>
            <p className="text-xs text-[#F4F1DE]/40 mt-2">
              Total height: {(pricing?.total_height || config.stack_count * 2)}ft
            </p>
          </div>

          {/* Glass Thickness */}
          <div className="mb-6 pt-2">
            <Label className="text-[#F4F1DE]/80 mb-3 block">Glass Thickness</Label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { mm: 6, tag: "Standard", blurb: "Geckos, corn snakes, bearded dragons" },
                { mm: 8, tag: "Upgrade", blurb: "Monitors, tegus, larger constrictors" },
                { mm: 10, tag: "Upgrade", blurb: "Heavy-duty: iguanas, large monitors" },
              ].map(({ mm, tag, blurb }) => (
                <button
                  key={mm}
                  onClick={() => setConfig(prev => ({ ...prev, glass_thickness: mm }))}
                  className={`py-2 px-2 border text-center text-sm relative flex flex-col gap-1 items-center justify-start ${
                    config.glass_thickness === mm
                      ? "border-[#E07A5F] bg-[#E07A5F]/10 text-[#E07A5F]"
                      : "border-white/10 bg-[#121212] text-[#F4F1DE]/60"
                  }`}
                  data-testid={`stack-glass-${mm}mm`}
                >
                  <span className="font-semibold">{mm}mm</span>
                  <span className={`text-[10px] leading-tight ${
                    config.glass_thickness === mm ? "text-[#E07A5F]/80" : "text-[#F4F1DE]/40"
                  }`}>
                    {blurb}
                  </span>
                  <span
                    className={`absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${
                      tag === "Standard" ? "bg-[#E07A5F] text-white" : "bg-amber-500/80 text-black"
                    }`}
                  >
                    {tag}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Stain Colors */}
          <div className="mb-8">
            <Label className="text-[#F4F1DE]/80 mb-3 block">Wood Stain (Reptile Safe Water Based - 32 Colours)</Label>
            
            {/* Category Filter */}
            <div className="flex flex-wrap gap-1 mb-3">
              <button
                onClick={() => setSelectedCategory("all")}
                className={`px-2 py-1 text-xs border ${
                  selectedCategory === "all"
                    ? "border-[#E07A5F] bg-[#E07A5F]/10 text-[#E07A5F]"
                    : "border-white/10 bg-[#121212] text-[#F4F1DE]/50"
                }`}
              >
                All
              </button>
              {STAIN_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-2 py-1 text-xs border ${
                    selectedCategory === cat.id
                      ? "border-[#E07A5F] bg-[#E07A5F]/10 text-[#E07A5F]"
                      : "border-white/10 bg-[#121212] text-[#F4F1DE]/50"
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
            
            {/* Color Swatches */}
            <div className="grid grid-cols-8 gap-2">
              {Object.entries(WOOD_STAINS)
                .filter(([_, stain]) => selectedCategory === "all" || stain.category === selectedCategory)
                .map(([key, stain]) => (
                <button
                  key={key}
                  onClick={() => setConfig(prev => ({ ...prev, stain_color: key }))}
                  className={`stain-swatch ${config.stain_color === key ? "selected" : ""}`}
                  style={{ backgroundColor: stain.hex }}
                  title={stain.name}
                  data-testid={`stack-stain-${key}`}
                />
              ))}
            </div>
            <p className="text-xs text-[#F4F1DE]/40 mt-2">
              Selected: {WOOD_STAINS[config.stain_color]?.name}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={handleAddToCart}
              disabled={loading}
              className="w-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white py-6 font-bold uppercase tracking-widest btn-glow"
              data-testid="add-stack-to-cart-btn"
            >
              {loading ? (
                <span className="spinner mr-2" />
              ) : (
                <ShoppingCart className="w-5 h-5 mr-2" />
              )}
              Add Stack to Cart
            </Button>
          </div>

          {/* Info */}
          <div className="mt-6 p-4 bg-[#0A0A0A] border border-white/10 text-xs text-[#F4F1DE]/50">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5 text-[#E07A5F]" />
              <p>
                Stacking units include a sturdy metal frame. Labour discount applied for repeated units.
                Perfect for breeders and multi-reptile households.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StackingPage;
